"use client";

import { Button } from "@/components/ui/button";
import { Drawer, DrawerContent, DrawerTrigger } from "@/components/ui/drawer";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import Link, { type LinkProps } from "next/link";
import { useRouter } from "next/navigation";
import * as React from "react";
import { HamburgerAnimated } from "./HamburgerAnimated";
import { menuItems } from "./navigation-menu";

export function MobileNavPopover() {
	const [open, setOpen] = React.useState(false);

	const onOpenChange = React.useCallback((open: boolean) => {
		setOpen(open);
	}, []);

	return (
		<Popover open={open} onOpenChange={onOpenChange}>
			<PopoverTrigger asChild>
				<Button
					variant="ghost"
					className={cn(
						"group h-8 gap-4 px-0 text-base items-center justify-center",
						"hover:bg-transparent focus-visible:bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 lg:hidden",
					)}
				>
					<HamburgerAnimated className="h-2 w-2" />
					<span className="sr-only">Toggle Menu</span>
				</Button>
			</PopoverTrigger>
			<PopoverContent className="z-10 bg-background/80 backdrop-blur-lg transform-none w-screen h-screen overflow-auto border-none">
				<div className="overflow-auto ml-4 mb-20">
					<div className="flex flex-col space-y-3 mb-4">
						{menuItems?.map(
							(item) =>
								item.href && (
									<MobileLink
										key={item.href}
										href={item.href}
										onOpenChange={setOpen}
										className={item.className || ""}
									>
										{item.label}
									</MobileLink>
								),
						)}
					</div>
					<div className="flex flex-col space-y-8">
						{menuItems?.map(
							(item) =>
								item?.items?.length && (
									<div
										key={item.href}
										className={cn("flex flex-col gap-4", item.className)}
									>
										<h4 className="text-2xl font-semibold">{item.label}</h4>
										{item?.items?.length &&
											item.items.map((item) => (
												<React.Fragment key={item.href}>
													{item.href && (
														<MobileLink
															href={item.href}
															onOpenChange={setOpen}
															className="flex flex-col gap-2 text-primary/80"
														>
															<div className="flex items-center gap-2">
																{item.icon && <item.icon className="h-4 w-4" />}
																{item.label}
															</div>
															{item.description && (
																<span className="block text-xs leading-none text-primary/50 no-underline group-hover:no-underline">
																	{item.description}
																</span>
															)}
														</MobileLink>
													)}
												</React.Fragment>
											))}
									</div>
								),
						)}
					</div>
				</div>
			</PopoverContent>
		</Popover>
	);
}

export function MobileNavDrawer() {
	const [open, setOpen] = React.useState(false);

	const onOpenChange = React.useCallback((open: boolean) => {
		setOpen(open);
	}, []);

	return (
		<Drawer open={open} onOpenChange={onOpenChange}>
			<DrawerTrigger asChild className="group">
				<Button
					variant="ghost"
					className="h-8 gap-4 px-0 text-base hover:bg-transparent focus-visible:bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 md:hidden"
				>
					<HamburgerAnimated />
					<span className="sr-only">Toggle Menu</span>
				</Button>
			</DrawerTrigger>
			<DrawerContent className="max-h-[80svh] p-0">
				<div className="overflow-auto p-6">
					<div className="flex flex-col space-y-3">
						{menuItems?.map(
							(item) =>
								item.href && (
									<MobileLink
										key={item.href}
										href={item.href}
										onOpenChange={setOpen}
									>
										{item.label}
									</MobileLink>
								),
						)}
					</div>
					<div className="flex flex-col space-y-2">
						{menuItems?.map((item) => (
							<div key={item.href} className="flex flex-col gap-4 pt-6">
								<h4 className="text-xl font-medium">{item.label}</h4>
								{item?.items?.length &&
									item.items.map((item) => (
										<React.Fragment key={item.href}>
											{item.href && (
												<MobileLink
													href={item.href}
													onOpenChange={setOpen}
													className="opacity-80"
												>
													{item.label}
													{item.description && (
														<span className="ml-2 rounded-md bg-[#adfa1d] px-1.5 py-0.5 text-xs leading-none text-[#000000] no-underline group-hover:no-underline">
															{item.description}
														</span>
													)}
												</MobileLink>
											)}
										</React.Fragment>
									))}
							</div>
						))}
					</div>
				</div>
			</DrawerContent>
		</Drawer>
	);
}

interface MobileLinkProps extends LinkProps {
	onOpenChange?: (open: boolean) => void;
	children: React.ReactNode;
	className?: string;
}

function MobileLink({
	href,
	onOpenChange,
	className,
	children,
	...props
}: MobileLinkProps) {
	const router = useRouter();
	return (
		<Link
			href={href}
			onClick={() => {
				router.push(href.toString());
				onOpenChange?.(false);
			}}
			className={cn("text-[1.15rem]", className)}
			{...props}
		>
			{children}
		</Link>
	);
}
